import { Flex, Icon, Text } from '@chakra-ui/react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import React, { useEffect, useId, useMemo, useState } from 'react'
import { flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useUserState } from '~/atoms/UserState'
import { CircleBadge } from '~/components/shared/CircleBadge'
import { CircleBadgeVariant } from '~/components/shared/componentStyles/CircleBadge.style'
import { MenuPositioningWrapper } from '~/components/shared/Dropdown/Menu'
import QueueStatusMenu from '~/components/shared/Dropdown/QueueStatusMenu'
import {
  isQueueAgentStatus,
  QueueAgentStatus,
  useCallQueue,
} from '~/hooks/useCallQueue'
import { useOutsideClick } from '~/hooks/useOutsideClick'
import { useGetCustomStatuses } from '~/queries/customStatusQueries'
import { castRef } from '~/utils/refUtils'

import { keyboardFocusRing } from '@mangovoice/design-system'

import {
  QueueControlButtonVariant,
  useQueueControlButtonStyle,
} from './componentStyles/QueueControlButton.style'

type StatusData = {
  label: string
  secondaryLabel?: string
  selectItemText: string
  badgeVariant?: CircleBadgeVariant
  buttonVariant: QueueControlButtonVariant
}

export const QueueControlButton = () => {
  const { t } = useTranslation()
  const [selectedValue, setSelectedValue] = useState<QueueAgentStatus | string>(
    QueueAgentStatus.OutOfQueue,
  )
  const [menuOpen, setMenuOpen] = useState(false)

  const { currentAgentStatus, updateStatus, formattedTime } = useCallQueue()

  const { pbxId } = useUserState()
  const { data: customStatuses } = useGetCustomStatuses(pbxId)

  useEffect(() => {
    if (currentAgentStatus !== selectedValue) {
      setSelectedValue(currentAgentStatus)
    }
  }, [currentAgentStatus])

  const containerRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const buttonId = useId()

  useOutsideClick({
    ref: castRef(containerRef),
    handler: () => {
      if (menuOpen) {
        setMenuOpen(false)
      }
    },
  })

  const handleSelect = (value: QueueAgentStatus | string) => {
    if (!isQueueAgentStatus(value)) {
      const status = customStatuses?.find((status) => status.id === value)
      if (status) {
        updateStatus(value as unknown as QueueAgentStatus, {
          id: value,
          label: status?.name ?? '',
          availability: status?.availability,
          startTime: new Date(),
        })
      }
    } else {
      updateStatus(value)
    }
    setMenuOpen(false)
    buttonRef.current?.focus()
  }

  // Tab must unmount the menu before the browser moves focus on from the
  // trigger, or focus lands on a menu item that is about to disappear.
  const handleKeyboardClose = () => {
    flushSync(() => setMenuOpen(false))
    buttonRef.current?.focus()
  }

  const statusToLabelData = useMemo(() => {
    const baseStatuses: Record<QueueAgentStatus, StatusData> = {
      [QueueAgentStatus.InQueue]: {
        label: t('queueControlButton.inQueue'),
        secondaryLabel: formattedTime,
        selectItemText: t('queueControlButton.joinQueue'),
        badgeVariant: CircleBadgeVariant.Green,
        buttonVariant: QueueControlButtonVariant.InQueue,
      },
      // this may be redundant since we have offline now. will need a little refactoring to remove
      [QueueAgentStatus.OutOfQueue]: {
        label: t('queueControlButton.outOfQueue'),
        secondaryLabel: undefined,
        selectItemText: t('queueControlButton.leaveQueue'),
        badgeVariant: undefined,
        buttonVariant: QueueControlButtonVariant.OutOfQueue,
      },
      [QueueAgentStatus.DoNotDisturb]: {
        label: t('queueControlButton.doNotDisturb'),
        secondaryLabel: undefined,
        selectItemText: t('queueControlButton.doNotDisturb'),
        badgeVariant: CircleBadgeVariant.Red,
        buttonVariant: QueueControlButtonVariant.DoNotDisturb,
      },
      [QueueAgentStatus.Offline]: {
        label: t('queueControlButton.offline'),
        secondaryLabel: undefined,
        selectItemText: t('queueControlButton.goOffline'),
        badgeVariant: undefined,
        buttonVariant: QueueControlButtonVariant.OutOfQueue,
      },
    }

    const customStatusesMap = customStatuses?.reduce(
      (acc, status) => {
        acc[status.id] = {
          label: status.name,
          secondaryLabel:
            formattedTime && selectedValue === status.id
              ? `${status.availability === 'Online' ? t('queueControlButton.online') : t('queueControlButton.offline')} - ${formattedTime}`
              : `${status.availability === 'Online' ? t('queueControlButton.online') : t('queueControlButton.offline')}`,
          selectItemText: status.name,
          badgeVariant:
            status.availability === 'Online'
              ? CircleBadgeVariant.Blue
              : undefined,
          buttonVariant:
            status.availability === 'Online'
              ? QueueControlButtonVariant.CustomOnline
              : QueueControlButtonVariant.CustomAway,
        }
        return acc
      },
      {} as Record<string, StatusData>,
    )

    return {
      ...baseStatuses,
      ...customStatusesMap,
    }
  }, [customStatuses, selectedValue, formattedTime, t])

  const getStatusData = (value: QueueAgentStatus | string): StatusData => {
    if (isQueueAgentStatus(value)) {
      return (
        statusToLabelData[value] ??
        statusToLabelData[QueueAgentStatus.OutOfQueue]
      )
    }
    return (
      (statusToLabelData as Record<string, StatusData>)[value] ??
      statusToLabelData[QueueAgentStatus.OutOfQueue]
    )
  }

  const currentStatusData = getStatusData(selectedValue)

  const styles = useQueueControlButtonStyle({
    variant: currentStatusData.buttonVariant,
  })

  return (
    <MenuPositioningWrapper ref={containerRef}>
      <Flex
        asChild
        css={styles.button}
        textAlign="start"
        {...keyboardFocusRing}
      >
        <button
          type="button"
          ref={buttonRef}
          id={buttonId}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((prev) => !prev)}
        >
          <Flex as="span" css={styles.buttonContentsContainer}>
            {currentStatusData.badgeVariant && (
              <CircleBadge variant={currentStatusData.badgeVariant} />
            )}
            <Flex as="span" css={styles.textContainer}>
              <Text as="span" css={styles.label}>
                {currentStatusData.label}
              </Text>
              {currentStatusData.secondaryLabel && (
                <Text as="span" css={styles.secondaryLabel}>
                  {currentStatusData.secondaryLabel}
                </Text>
              )}
            </Flex>
          </Flex>
          <Icon
            as={menuOpen ? ChevronUp : ChevronDown}
            css={styles.chevronIcon}
            strokeWidth={3}
            aria-hidden
          />
        </button>
      </Flex>
      {menuOpen && (
        <QueueStatusMenu
          customStatuses={customStatuses ?? []}
          selectedValue={selectedValue}
          handleSelect={handleSelect}
          anchorRef={castRef(buttonRef)}
          onKeyboardClose={handleKeyboardClose}
          labelledBy={buttonId}
        />
      )}
    </MenuPositioningWrapper>
  )
}
